export interface AudioClip {
    id: string;
    url: string;
    audio?: HTMLAudioElement;
    loaded: boolean;
}

export class AudioManager {
    private audioClips: Map<string, AudioClip> = new Map();
    private isInitialized = false;
    private instanceId: string;
    private currentlyPlaying: HTMLAudioElement | null = null;
    private currentAudioId: string | null = null;

    constructor() {
        this.instanceId = Math.random().toString(36).substr(2, 9);
        console.log(`🏗️ AudioManager instance created: ${this.instanceId}`);
    }

    // Audio file mappings
    private readonly audioFiles = {
        // Static messages
        messages: {
            ask_word: '/audio/messages/ask_word.mp3',
            greeting_and_ask_name: '/audio/messages/greeting_and_ask_name.mp3', // "שלום! איזה כיף שאנחנו הולכים לכתוב מילים ביחד! איך קוראים לך?"
            lets_start: '/audio/messages/lets_start.mp3', // "קדימה! בואו נתחיל!"
            // Legacy files for backward compatibility
            ask_gender: '/audio/messages/ask_gender.mp3',
            ask_name: '/audio/messages/ask_name.mp3',
        },
        
        // Encouragement variations
        encouragement: {
            1: '/audio/encouragement/encouragement_1.mp3',
            2: '/audio/encouragement/encouragement_2.mp3',
            3: '/audio/encouragement/encouragement_3.mp3',
            4: '/audio/encouragement/encouragement_4.mp3',
            5: '/audio/encouragement/encouragement_5.mp3',
            6: '/audio/encouragement/encouragement_6.mp3',
            7: '/audio/encouragement/encouragement_7.mp3',
            8: '/audio/encouragement/encouragement_8.mp3',
            9: '/audio/encouragement/encouragement_9.mp3',
            10: '/audio/encouragement/encouragement_10.mp3',
        },
        
        // Celebration variations
        celebration: {
            1: '/audio/celebration/celebration_1.mp3', // "אדיר! הקלדתם את המילה בהצלחה! אתם אלופים!"
            // 2: celebration_2.mp3 is empty, skipping
            3: '/audio/celebration/celebration_3.mp3', // "מושלם! המילה הוקלדה נהדר!"
        },
        
        // Template parts for dynamic content
        templates: {
            word_intro_suffix: '/audio/templates/word_intro_suffix.mp3', // "זו מילה מעולה. קדימה. בואו נחפש את כל האותיות שלה במקלדת"
            almost_prefix: '/audio/templates/almost_prefix.mp3', // "כמעט!"
            // greeting_prefix is empty, will fall back to TTS
        },
        
        // Letter guidance
        letters: {
            'א': '/audio/letters/guidance_א.mp3',
            'ב': '/audio/letters/guidance_ב.mp3',
            'ו': '/audio/letters/guidance_ו.mp3',
            'ז': '/audio/letters/guidance_ז.mp3',
            'ח': '/audio/letters/guidance_ח.mp3',
            'י': '/audio/letters/guidance_י.mp3',
            'ל': '/audio/letters/guidance_ל.mp3',
            'ם': '/audio/letters/guidance_ם.mp3',
            'ע': '/audio/letters/guidance_ע.mp3',
            'פ': '/audio/letters/guidance_פ.mp3',
            'ר': '/audio/letters/guidance_ר.mp3',
            'ש': '/audio/letters/guidance_ש.mp3',
        }
    };

    async initialize(): Promise<void> {
        if (this.isInitialized) {
            console.log(`AudioManager ${this.instanceId} already initialized, skipping`);
            return;
        }

        // Check if initialization is already in progress
        if (this.initializationPromise) {
            console.log(`AudioManager ${this.instanceId} initialization in progress, waiting...`);
            return this.initializationPromise;
        }

        console.log(`🎵 Initializing AudioManager instance: ${this.instanceId}`);
        
        this.initializationPromise = this.doInitialize();
        return this.initializationPromise;
    }

    private initializationPromise: Promise<void> | null = null;

    private async doInitialize(): Promise<void> {
        try {
            await this.preloadCriticalAudio();
            this.isInitialized = true;
            console.log(`✅ AudioManager ${this.instanceId} initialized successfully`);
        } catch (error) {
            console.error(`❌ Failed to initialize AudioManager ${this.instanceId}:`, error);
            // Continue anyway - will fall back to TTS
            this.isInitialized = true;
        } finally {
            this.initializationPromise = null;
        }
    }

    private async preloadCriticalAudio(): Promise<void> {
        // Preload ALL audio files for instant playback using more compatible approach
        const allFiles: string[] = [];
        
        // Add message files
        for (const url of Object.keys(this.audioFiles.messages)) {
            const value = (this.audioFiles.messages as any)[url];
            if (value) allFiles.push(value);
        }
        
        // Add template files
        for (const url of Object.keys(this.audioFiles.templates)) {
            const value = (this.audioFiles.templates as any)[url];
            if (value) allFiles.push(value);
        }
        
        // Add encouragement files
        for (const url of Object.keys(this.audioFiles.encouragement)) {
            const value = (this.audioFiles.encouragement as any)[url];
            if (value) allFiles.push(value);
        }
        
        // Add celebration files
        for (const url of Object.keys(this.audioFiles.celebration)) {
            const value = (this.audioFiles.celebration as any)[url];
            if (value) allFiles.push(value);
        }
        
        // Add letter files
        for (const url of Object.keys(this.audioFiles.letters)) {
            const value = (this.audioFiles.letters as any)[url];
            if (value) allFiles.push(value);
        }

        console.log(`Preloading ${allFiles.length} audio files...`);
        
        // Use Promise.all instead of Promise.allSettled for compatibility
        try {
            const loadPromises = allFiles.map(url => this.loadAudioFile(url).catch(error => {
                console.warn(`Failed to load audio file: ${url}`, error);
                return null;
            }));
            
            const results = await Promise.all(loadPromises);
            const loaded = results.filter(r => r !== null).length;
            const failed = results.filter(r => r === null).length;
            console.log(`Audio preloading complete: ${loaded} loaded, ${failed} failed`);
        } catch (error) {
            console.error('Audio preloading failed:', error);
        }
    }

    private async loadAudioFile(url: string): Promise<AudioClip> {
        const id = url.split('/').pop()?.replace('.mp3', '') || url;
        
        if (this.audioClips.has(id)) {
            return this.audioClips.get(id)!;
        }

        // First check if the file exists and has content
        try {
            console.log(`📡 Checking audio file: ${url}`);
            const response = await fetch(url, { method: 'HEAD' });
            const contentLength = response.headers.get('content-length');
            
            console.log(`📊 Response for ${url}:`, {
                ok: response.ok,
                status: response.status,
                contentLength: contentLength
            });
            
            if (!response.ok || contentLength === '0' || contentLength === null) {
                console.warn(`Audio file is empty or missing: ${url}`);
                throw new Error('Empty or missing audio file');
            }
            
            console.log(`✅ File check passed for: ${url} (${contentLength} bytes)`);
        } catch (error) {
            console.warn(`Failed to check audio file: ${url}`, error);
            throw error;
        }

        const audio = new Audio(url);
        const clip: AudioClip = {
            id,
            url,
            audio,
            loaded: false
        };

        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                console.warn(`Audio loading timeout: ${url}`);
                reject(new Error('Audio loading timeout'));
            }, 10000); // Increased timeout

            // Try multiple event listeners for better compatibility
            const onSuccess = () => {
                clearTimeout(timeout);
                clip.loaded = true;
                this.audioClips.set(id, clip);
                console.log(`✅ Audio loaded successfully: ${id}`);
                resolve(clip);
            };

            const onError = (error: any) => {
                clearTimeout(timeout);
                console.warn(`Failed to load audio file: ${url}`, error);
                reject(error);
            };

            // Listen for multiple events that indicate readiness
            audio.addEventListener('canplaythrough', onSuccess, { once: true });
            audio.addEventListener('loadeddata', onSuccess, { once: true });
            audio.addEventListener('error', onError, { once: true });

            // Set properties that help with loading
            audio.preload = 'auto';
            audio.crossOrigin = 'anonymous';
            
            // Start loading
            audio.load();
            
            // Fallback: if file exists and passed validation, assume it works
            setTimeout(() => {
                if (!clip.loaded) {
                    console.log(`📋 Fallback loading for: ${id}`);
                    onSuccess();
                }
            }, 2000);
        });
    }

    // Stop any currently playing audio
    private stopCurrentAudio(): void {
        if (this.currentlyPlaying) {
            console.log(`🛑 Stopping currently playing audio: ${this.currentAudioId}`);
            this.currentlyPlaying.pause();
            this.currentlyPlaying.currentTime = 0;
            this.currentlyPlaying = null;
            this.currentAudioId = null;
        }
    }

    // Public method to stop all audio
    stopAllAudio(): void {
        this.stopCurrentAudio();
        console.log('🔇 All audio stopped');
    }

    async playAudio(audioId: string): Promise<boolean> {
        try {
            const clip = this.audioClips.get(audioId);
            if (clip && clip.loaded && clip.audio) {
                // Stop any currently playing audio first
                this.stopCurrentAudio();
                
                console.log(`🎵 Playing audio: ${audioId}`);
                clip.audio.currentTime = 0;
                
                // Set this as the currently playing audio
                this.currentlyPlaying = clip.audio;
                this.currentAudioId = audioId;
                
                // Return a promise that resolves when audio finishes playing
                return new Promise((resolve) => {
                    const audio = clip.audio!;
                    
                    // Handle audio completion
                    const onAudioEnd = () => {
                        console.log(`✅ Audio playback completed: ${audioId}`);
                        audio.removeEventListener('ended', onAudioEnd);
                        audio.removeEventListener('error', onAudioError);
                        audio.removeEventListener('pause', onAudioPause);
                        
                        // Clear currently playing if this is still the current audio
                        if (this.currentlyPlaying === audio) {
                            this.currentlyPlaying = null;
                            this.currentAudioId = null;
                        }
                        resolve(true);
                    };
                    
                    // Handle audio errors
                    const onAudioError = (error: any) => {
                        console.warn(`❌ Audio playback error for ${audioId}:`, error);
                        audio.removeEventListener('ended', onAudioEnd);
                        audio.removeEventListener('error', onAudioError);
                        audio.removeEventListener('pause', onAudioPause);
                        
                        // Clear currently playing if this is still the current audio
                        if (this.currentlyPlaying === audio) {
                            this.currentlyPlaying = null;
                            this.currentAudioId = null;
                        }
                        resolve(false);
                    };
                    
                    // Handle audio pause (when stopped by another audio)
                    const onAudioPause = () => {
                        console.log(`⏸️ Audio interrupted: ${audioId}`);
                        audio.removeEventListener('ended', onAudioEnd);
                        audio.removeEventListener('error', onAudioError);
                        audio.removeEventListener('pause', onAudioPause);
                        
                        // Don't clear currently playing here as it may be set to a new audio
                        resolve(false); // Resolve with false since it was interrupted
                    };
                    
                    // Add event listeners
                    audio.addEventListener('ended', onAudioEnd, { once: true });
                    audio.addEventListener('error', onAudioError, { once: true });
                    audio.addEventListener('pause', onAudioPause, { once: true });
                    
                    // Start playback
                    audio.play().catch((playError) => {
                        console.warn(`❌ Failed to start audio playback for ${audioId}:`, playError);
                        audio.removeEventListener('ended', onAudioEnd);
                        audio.removeEventListener('error', onAudioError);
                        audio.removeEventListener('pause', onAudioPause);
                        
                        // Clear currently playing if this is still the current audio
                        if (this.currentlyPlaying === audio) {
                            this.currentlyPlaying = null;
                            this.currentAudioId = null;
                        }
                        resolve(false);
                    });
                });
            } else {
                console.warn(`❌ Audio not available: ${audioId}`);
                return false;
            }
        } catch (error) {
            console.warn(`Failed to play audio: ${audioId}`, error);
            return false;
        }
    }

    // High-level methods for common use cases
    async playMessage(messageType: keyof typeof this.audioFiles.messages): Promise<boolean> {
        const url = this.audioFiles.messages[messageType];
        console.log(`🎵 Attempting to play message: ${messageType} -> ${url}`);
        if (!url) return false;
        
        const audioId = url.split('/').pop()?.replace('.mp3', '') || '';
        return this.playAudio(audioId);
    }

    async playEncouragement(index?: number): Promise<boolean> {
        const encouragementIndex = index || Math.floor(Math.random() * 10) + 1;
        const url = this.audioFiles.encouragement[encouragementIndex as keyof typeof this.audioFiles.encouragement];
        if (!url) return false;
        
        const audioId = url.split('/').pop()?.replace('.mp3', '') || '';
        return this.playAudio(audioId);
    }

    async playCelebration(index?: number): Promise<boolean> {
        const availableCelebrations = [1, 3]; // Only available celebration files
        const celebrationIndex = index || availableCelebrations[Math.floor(Math.random() * availableCelebrations.length)];
        const url = this.audioFiles.celebration[celebrationIndex as keyof typeof this.audioFiles.celebration];
        if (!url) return false;
        
        const audioId = url.split('/').pop()?.replace('.mp3', '') || '';
        return this.playAudio(audioId);
    }

    async playLetterGuidance(letter: string, wrongKey?: string): Promise<boolean> {
        try {
            if (wrongKey) {
                // Try to play "כמעט!" first, then the letter guidance
                const almostPlayed = await this.playAudio('almost_prefix');
                if (almostPlayed) {
                    // Wait a bit before playing the letter guidance
                    await new Promise(resolve => setTimeout(resolve, 800));
                }
            }
            
            // Play the letter guidance - type-safe lookup
            const letters = this.audioFiles.letters as { [key: string]: string };
            const url = letters[letter];
            if (!url) return false;
            
            const audioId = url.split('/').pop()?.replace('.mp3', '') || '';
            return this.playAudio(audioId);
        } catch (error) {
            console.error('Failed to play letter guidance with wrong key handling:', error);
            return false;
        }
    }

    // Enhanced letter guidance that handles "כמעט" (almost) messages
    async playLetterGuidanceWithPrefix(letter: string, wrongKey?: string, voiceService?: any): Promise<void> {
        try {
            if (wrongKey) {
                // Try to play "כמעט!" from audio first
                const almostPlayed = await this.playAudio('almost_prefix');
                
                // No TTS fallback needed - audio files handle this
                
                // Wait a bit before continuing
                await new Promise(resolve => setTimeout(resolve, almostPlayed ? 800 : 1000));
            }
            
            // Try to play letter guidance from audio (no TTS fallback needed)
            await this.playLetterGuidance(letter);
        } catch (error) {
            console.error('Failed to play letter guidance with prefix:', error);
            // No TTS fallback - audio files handle all letter guidance
        }
    }

    // Template-based dynamic content
    async playWordIntroduction(word: string, voiceService: any): Promise<void> {
        try {
            // First, speak the word using forced TTS (bypasses global disable)
            const ttsPromise = voiceService.speakForced(word);
            
            // Wait for TTS to complete, or minimum delay if TTS is disabled
            await Promise.race([
                ttsPromise,
                new Promise(resolve => setTimeout(resolve, 1000)) // Minimum 1 second delay
            ]);
            
            // Then play the pre-recorded suffix
            await new Promise(resolve => setTimeout(resolve, 300));
            const played = await this.playAudio('word_intro_suffix');
            
            if (!played) {
                // Fallback to full TTS
                const fullMessage = `${word} זו מילה מעולה. קדימה. בואו נחפש את כל האותיות שלה במקלדת`;
                await voiceService.speak(fullMessage);
            }
        } catch (error) {
            console.error('Failed to play word introduction:', error);
            // Fallback to TTS
            const fullMessage = `${word} זו מילה מעולה. קדימה. בואו נחפש את כל האותיות שלה במקלדת`;
            await voiceService.speak(fullMessage);
        }
    }

    async playGreeting(childName: string, voiceService: any): Promise<void> {
        try {
            // Play "היי" from audio
            const greetingPlayed = await this.playAudio('greeting_prefix');
            
            if (greetingPlayed) {
                await new Promise(resolve => setTimeout(resolve, 200));
                // Then speak the name and rest with TTS
                await voiceService.speak(`${childName}! רוצה שנתאמן קצת בכתיבת מילים?`);
            } else {
                // Fallback to full TTS
                await voiceService.speak(`היי ${childName}! רוצה שנתאמן קצת בכתיבת מילים?`);
            }
        } catch (error) {
            console.error('Failed to play greeting:', error);
            // Fallback to TTS
            await voiceService.speak(`היי ${childName}! רוצה שנתאמן קצת בכתיבת מילים?`);
        }
    }

    // Lazy loading for non-critical audio
    async ensureAudioLoaded(audioPath: string): Promise<boolean> {
        const audioId = audioPath.split('/').pop()?.replace('.mp3', '') || '';
        
        if (this.audioClips.has(audioId)) {
            return this.audioClips.get(audioId)!.loaded;
        }

        try {
            await this.loadAudioFile(audioPath);
            return true;
        } catch (error) {
            return false;
        }
    }

    // Check if audio file exists before attempting to load
    async checkAudioAvailability(audioPath: string): Promise<boolean> {
        try {
            const response = await fetch(audioPath, { method: 'HEAD' });
            return response.ok;
        } catch {
            return false;
        }
    }
}